import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle as AlertTriangleIcon, Search, ClipboardList, Hourglass } from 'lucide-react';
import { getEscalationLogs, deleteEscalationLog } from '../data';
import './AgentPages.css';

export default function AgentEscalationLogs() {
  const { agent, agentId } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadLogs();
  }, [agentId]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await getEscalationLogs(agentId);
      setLogs(data || []);
    } catch (err) {
      console.error('Failed to load escalation logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (logId) => {
    if (!confirm('Delete this escalation log?')) return;
    try {
      await deleteEscalationLog(agentId, logId);
      setLogs(logs.filter(l => l.id !== logId));
    } catch (err) {
      console.error('Failed to delete log:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.trigger?.toLowerCase().includes(query) ||
      log.customer_email?.toLowerCase().includes(query) ||
      log.escalated_to?.toLowerCase().includes(query) ||
      log.handoff_context?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="config-page">
      <div className="config-header">
        <h1>Escalation Logs</h1>
        <p>Track when and why your agent escalates issues to human operators. Each escalation includes the trigger, customer details, and handoff context.</p>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Search size={18} /></div>
          <div className="section-text">
            <h2>Search Escalations</h2>
            <p>Find specific escalations by trigger, email, or context</p>
          </div>
        </div>

        <div className="form-group">
          <input
            type="text"
            className="input"
            placeholder="Search by trigger, customer email, or context..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="escalation-stats">
          <div className="escalation-stat">
            <span className="stat-number">{logs.length}</span>
            <span className="stat-label">Total Escalations</span>
          </div>
          <div className="escalation-stat">
            <span className="stat-number">
              {logs.filter(l => {
                const date = new Date(l.timestamp);
                const now = new Date();
                return date.toDateString() === now.toDateString();
              }).length}
            </span>
            <span className="stat-label">Today</span>
          </div>
          <div className="escalation-stat">
            <span className="stat-number">
              {logs.filter(l => {
                const date = new Date(l.timestamp);
                const now = new Date();
                const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                return date >= weekAgo;
              }).length}
            </span>
            <span className="stat-label">This Week</span>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><ClipboardList size={18} /></div>
          <div className="section-text">
            <h2>Escalation History</h2>
            <p>All escalations are logged automatically when your agent hands off to a human</p>
          </div>
        </div>

        {loading ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Hourglass size={24} /></div>
            <h3>Loading escalation logs...</h3>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><ClipboardList size={24} /></div>
            <h3>No escalations yet</h3>
            <p>When your agent escalates an issue to a human, it will appear here with full context.</p>
          </div>
        ) : (
          <div className="escalation-logs-list">
            <AnimatePresence>
              {filteredLogs.map((log, index) => (
                <motion.div
                  key={log.id}
                  className="escalation-log-card"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <div 
                    className="escalation-log-header"
                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  >
                    <div className="escalation-log-main">
                      <div className="escalation-log-trigger">
                        <span className="trigger-icon"><AlertTriangleIcon size={14} /></span>
                        <span className="trigger-text">{log.trigger || 'Unknown trigger'}</span>
                      </div>
                      <div className="escalation-log-meta">
                        <span className="meta-item">
                          <span className="meta-icon"></span>
                          {log.customer_email || 'Unknown'}
                        </span>
                        <span className="meta-item">
                          <span className="meta-icon"></span>
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    </div>
                    <span className="escalation-expand-icon">{expandedId === log.id ? '−' : '+'}</span>
                  </div>

                  {expandedId === log.id && (
                    <motion.div 
                      className="escalation-log-details"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <div className="escalation-detail-grid">
                        <div className="escalation-detail-item">
                          <h5>Escalated To</h5>
                          <p>{log.escalated_to || 'Not specified'}</p>
                        </div>
                        <div className="escalation-detail-item">
                          <h5>Customer Email</h5>
                          <p>{log.customer_email || 'Unknown'}</p>
                        </div>
                        <div className="escalation-detail-item">
                          <h5>Timestamp</h5>
                          <p>{formatDate(log.timestamp)}</p>
                        </div>
                        <div className="escalation-detail-item">
                          <h5>Subject</h5>
                          <p>{log.subject || 'No subject'}</p>
                        </div>
                      </div>

                      {log.handoff_context && (
                        <div className="escalation-detail-section">
                          <h5>Handoff Context</h5>
                          <div className="handoff-context-box">
                            {log.handoff_context}
                          </div>
                        </div>
                      )}

                      {log.conversation_summary && (
                        <div className="escalation-detail-section">
                          <h5>Conversation Summary</h5>
                          <div className="conversation-summary-box">
                            {log.conversation_summary}
                          </div>
                        </div>
                      )}

                      <div className="escalation-log-actions">
                        <button 
                          className="btn btn-sm btn-outline"
                          onClick={(e) => { e.stopPropagation(); handleDelete(log.id); }}
                          style={{ color: '#dc2626', borderColor: '#fecaca' }}
                        >
                          Delete Log
                        </button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
